/**
 * 모니터링 ▸ 사업장 현황 — 화면이 **어느 지역 · 어느 공장 · 어느 건물**에 있는지로 본다.
 *
 * 지역 › 공장 › 건물은 층이 정해져 있고, 각 층의 이름은 [사업장·위치]·[사업장 구성]에서 붙인다 — 건물은 각 서버의 화면
 * 위치 그대로, 지역·공장은 서버(출처)마다, 필요하면 건물마다. 묶는 규칙은 `sites.ts`, 저장은 `/api/v1/sites`.
 *
 * **보기가 둘이다**(2026-10-22 사용자 지시 "지도 기반 모니터링은 다른 signage 서버와 연동이 되었을때 사용하는 통합
 * 모니터링으로 사용하고 이전의 draw 타입의 모니터링을 기본으로 사용한다"). 2026-10-28 부터 **메뉴가 나뉜다**(사용자 지시
 * "통합 관리 서버가 적용되어 있으면 관리자 권한의 계정에도 통합 모니터링 메뉴가 보여야 한다. 메뉴 나누어서") — 예전에는
 * 모니터링 도구 줄의 [그림판 | 통합 모니터링] 전환이었다(`?view=map`, 지금은 `/integrated` 로 옮겨 준다).
 *
 * - **모니터링**(`/wall`, 그림판) — 이 서버의 화면만, 지역·공장·건물 상자 안에 화면 노드(`components/SiteBoard.tsx`, [배치 편집]).
 * - **통합 모니터링**(`/integrated`, 지도) — 통합 관제에 등록한 원격 서버까지 한 지도에, 건물 주소 자리마다 화면 노드 카드
 *   (`components/SiteMap.tsx`). 원격 서버가 하나라도 등록돼 있고 통합 관제 모듈이 켜진 서버에서, 화면 관리 권한이 있는
 *   계정(관리자)에게 메뉴가 보인다(`/me` 의 `features.hub_monitor`). 원격 서버의 화면 목록은 이 보기에서만 묻는다.
 *
 * 어느 보기든 왼쪽 나무와 위쪽 도구 줄이 떠 있다. 나무의 맨 위는 **사업장 이름**(사업장·위치 메뉴에서 정한다, 비면 "전체
 * 사업장")이고 그 아래가 지역 › 공장 › 화면이다. 범위를 고르면 그 자리로 날아가고, 화면을 고르면 오른쪽에 그 화면의 판이
 * 열린다(지도에서는 공장을 골라도 그 공장의 판). 고른 범위는 주소에 남는다(`?region=&site=&display=`).
 *
 * 숫자는 온라인/전체와 오프라인이고, 색은 오프라인 비율이다. 연결이 끊긴 서버의 숫자는 끊기기 전의 것이라
 * 회색으로 흐리게 남긴다(화면 장애로 세지 않는다 — 통합 관제와 같은 규칙).
 *
 * 썸네일은 본 서버의 화면만 그린다 — 화면 그림 목록(`/displays/wall`)을 **캡처를 청하지 않고** 부른다.
 * 원격 서버의 그림은 그 서버의 저장소 주소라 이 브라우저가 닿는다는 보장이 없어(망이 다르다) 상태만 그린다.
 */
import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Building, Building2, ChevronRight, MapPin, Settings2, WifiOff, X } from 'lucide-react';
import type { Me, SignageOneClient, SiteAddress, SiteEntry } from '../api/client.js';
import { can } from '../capabilities.js';
import { navCapabilities } from '../nav.js';
import { useAction, useAsync, usePolling } from '../hooks.js';
import { Badge, Empty, ErrorNote, Loading, Modal, relativeTime, StatusDot } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { Disclosure } from '../components/Disclosure.js';
import { compareLocationNames } from '../components/locationOptions.js';
import { useUrlQuery } from '../components/urlState.js';
import SiteBoard from '../components/SiteBoard.js';
import { CountLine, DisplayNode, HealthBar, NodeShot, sourceLabel } from '../components/siteParts.js';
import { AddressDialog } from '../components/AddressDialog.js';
import type { AddressPoint, AddressPointItem } from '../components/siteMapStyle.js';
import { useSiteLabels } from '../components/siteLabelsContext.js';
import { fillSiteLabels, unassignedLocation, unnamedRegion } from '../siteLabels.js';
import {
  addressLine,
  buildSiteTree,
  draftFromEntries,
  factoryDisplays,
  healthTone,
  localDisplay,
  LOCAL_SITE_NAME,
  LOCAL_SOURCE,
  mergeSiteEntries,
  ownBuildingAddress,
  pointOf,
  remoteDisplay,
  remoteSource,
  resolveScope,
  scopeFromQuery,
  scopeQuery,
  siteTitle,
  sourceBaseAddress,
  withBuildingAddress,
  type BuildingNode,
  type Counts,
  type FactoryNode,
  type RegionNode,
  type ResolvedScope,
  type SiteDisplay,
  type SiteDisplayInput,
  type SiteDraft,
  type SiteScope,
  type SiteSource,
  type SitesView,
} from '../sites.js';

const POLL_MS = 30_000;
/** 본 서버 화면 목록의 상한. 넘으면 숫자는 집계로 세고 노드는 받은 만큼만 그린다. */
const LOCAL_LIMIT = 200;
/** 원격 서버 한 대에서 받을 쪽 수(쪽당 200대). */
const REMOTE_PAGES = 5;

/** 지도(MapLibre·타일 읽기)는 무겁다 — 통합 모니터링을 열 때만 받는다. */
const SiteMap = lazy(() => import('../components/SiteMap.js'));

const TONE_DOT: Record<ReturnType<typeof healthTone>, string> = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  danger: 'bg-rose-500',
  muted: 'bg-slate-300',
};

interface RemoteList {
  items: SiteDisplayInput[];
  truncated: boolean;
}

/** 사업장 현황 한 판 — 꽉 찬 그림판(또는 통합 모니터링의 지도)과 그 위에 뜬 왼쪽 나무·도구 줄. */
export function SitesWorkspace({ client, me, view }: { client: SignageOneClient; me: Me; view: SitesView }): ReactNode {
  const url = useUrlQuery();
  // 원격 서버는 통합 모니터링(지도)만 그린다 — 그 메뉴는 볼 수 있는 계정에만 열린다(`feature:hub_monitor`).
  const seesHub = view === 'map' && navCapabilities(me).includes('feature:hub_monitor');
  const canEdit = can(me, 'fleet:write') && !me.scope;
  const sites = useAsync(() => client.sites(), [client]);
  const local = useAsync(() => client.fleetSummary({ locations: true }), [client]);
  const wall = useAsync(() => client.wall({ capture: 'false', hidden: 'exclude', limit: String(LOCAL_LIMIT) }), [client]);
  const hub = useAsync(() => (seesHub ? client.listHubServers() : Promise.resolve(null)), [client, seesHub]);
  // 연결이 살아 있는 원격 서버만 목록을 묻는다 — 끊긴 서버는 묻는 동안 5초씩 기다리기만 한다. 그림판은 이 서버만 그리므로 묻지 않는다.
  const reachable =
    view === 'map'
      ? (hub.data?.items ?? []).filter((server) => server.state === 'ok' && !server.stale).map((server) => server.id)
      : [];
  const reachableKey = reachable.join(',');
  // 어느 서버들의 목록인지(`key`)를 함께 돌려준다 — 서버 목록이 바뀐 직후에는 앞 결과가 남아 있어 "다 받았다" 를 가를 수 없다.
  const remote = useAsync<{ key: string; lists: Record<string, RemoteList | null> }>(async () => {
    const out: Record<string, RemoteList | null> = {};
    await Promise.all(
      reachable.map(async (id) => {
        try {
          const items: SiteDisplayInput[] = [];
          let cursor: string | null = null;
          let pages = 0;
          do {
            const page = await client.hubDisplays(id, { cursor });
            items.push(...page.items.map(remoteDisplay));
            cursor = page.next_cursor;
            pages += 1;
          } while (cursor && pages < REMOTE_PAGES);
          out[id] = { items, truncated: Boolean(cursor) };
        } catch {
          // 목록을 못 받으면 숫자는 수집기의 집계로 남는다 — 노드만 못 그린다.
          out[id] = null;
        }
      }),
    );
    return { key: reachableKey, lists: out };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, reachableKey]);
  usePolling(() => {
    local.reload();
    wall.reload();
    hub.reload();
    remote.reload();
  }, POLL_MS);
  const [configuring, setConfiguring] = useState(false);
  /**
   * 주소 창을 연 건물 — 모니터링에서 곧바로 그 건물의 주소를 저장한다(2026-10-22 사용자 지시 "모니터링 화면에서 각 화면을
   * 표시하기 위해 화면이 위치한 건물의 주소를 저장"). 사업장·위치 표와 같은 줄(`/api/v1/sites` 의 그 건물 줄)을 고친다.
   */
  const [addressing, setAddressing] = useState<BuildingNode | null>(null);
  const addressAction = useAction();
  const saveBuildingAddress = (building: BuildingNode, next: SiteAddress | null): void => {
    if (!sites.data) return;
    void addressAction.run(
      () =>
        client.saveSites({
          entries: withBuildingAddress(sites.data!.entries, building.source.id, building.location, next),
          revision: sites.data!.revision,
        }),
      () => sites.reload(),
      next ? `'${building.name}' 주소를 저장했습니다` : `'${building.name}' 주소를 지웠습니다 — 기본 주소를 따릅니다`,
    );
  };
  /** 주소를 고칠 수 있는가 — 사업장 구성 저장(`PUT /sites`)과 같은 조건. */
  const editAddress = canEdit && sites.data ? (building: BuildingNode) => setAddressing(building) : undefined;

  const sources = useMemo<SiteSource[]>(() => {
    const cards = wall.data?.items ?? null;
    const list: SiteSource[] = [
      {
        id: LOCAL_SOURCE,
        name: LOCAL_SITE_NAME,
        kind: 'local',
        state: 'ok',
        stale: false,
        asOf: wall.data?.as_of ?? null,
        locations: local.data?.locations ?? [],
        displays: cards ? cards.map(localDisplay) : null,
        truncated: cards ? cards.length >= LOCAL_LIMIT : false,
      },
    ];
    for (const server of hub.data?.items ?? []) {
      const listed = remote.data?.lists[server.id] ?? null;
      list.push({ ...remoteSource(server), displays: listed?.items ?? null, truncated: listed?.truncated ?? false });
    }
    return list;
  }, [local.data, wall.data, hub.data, remote.data]);
  const entries = sites.data?.entries ?? [];
  const title = siteTitle(sites.data?.name);
  // 그림판은 이 서버의 화면만 — 여러 서버를 모아 보는 것은 통합 모니터링의 일이다.
  const shown = useMemo(() => (view === 'map' ? sources : sources.filter((source) => source.kind === 'local')), [view, sources]);
  const labels = useSiteLabels();
  const tree = useMemo(() => buildSiteTree(shown, entries, labels), [shown, entries, labels]);
  const resolved = resolveScope(tree, scopeFromQuery((key) => (url.has(key) ? url.get(key) : null)));
  /** 같은 범위를 다시 골라도 카메라가 다시 맞춰 가게 — 고를 때마다 센다. */
  const [flights, setFlights] = useState(0);
  const select = (scope: SiteScope): void => {
    setFlights((count) => count + 1);
    url.set(scopeQuery(scope));
  };

  const totals = tree.reduce<Counts>((sum, region) => ({ total: sum.total + region.total, online: sum.online + region.online, offline: sum.offline + region.offline }), { total: 0, online: 0, offline: 0 });
  const cut = shown.filter((source) => source.kind === 'remote' && source.stale);
  // 지도는 원격 서버 목록이 온 뒤에 세운다 — 이 서버만 먼저 그렸다가 원격 서버가 늦게 들어와 맞춘 자리가 어긋나지 않게.
  const hubPending = seesHub && !hub.data && !hub.error;
  const loading = (!local.data && local.loading) || (!sites.data && sites.loading) || hubPending;

  // 처음 맞추기는 다 받은 뒤에 — 원격 서버의 지역이 늦게 들어와 그림이 커지면 맞춘 자리가 어긋난다.
  const settled =
    Boolean(local.data || local.error) &&
    Boolean(wall.data || wall.error) &&
    Boolean(sites.data || sites.error) &&
    (view === 'board' || ((!seesHub || Boolean(hub.data || hub.error)) && (reachable.length === 0 || remote.data?.key === reachableKey || Boolean(remote.error))));
  const display = resolved.display;
  const displayBuilding =
    display && resolved.factory ? (resolved.factory.buildings.find((building) => building.key === `${display.source.id}\u0000${display.location}`) ?? null) : null;

  return (
    <div className="site-stage">
      {loading ? (
        <div className="site-stage-center">
          <Loading />
        </div>
      ) : tree.length === 0 ? (
        <div className="site-stage-center">
          <Empty title="표시할 화면이 없습니다" hint={`화면을 등록하면 ${labels.region} › ${labels.site} › ${labels.location}별로 집계됩니다.`} />
        </div>
      ) : view === 'board' ? (
        <SiteBoard
          client={client}
          tree={tree}
          resolved={resolved}
          flights={flights}
          settled={settled}
          onSelect={select}
          canEdit={canEdit}
          onConfigure={() => setConfiguring(true)}
        />
      ) : (
        <Suspense
          fallback={
            <div className="site-stage-center">
              <Loading />
            </div>
          }
        >
          <SiteMap
            tree={tree}
            resolved={resolved}
            flights={flights}
            settled={settled}
            onSelect={select}
            renderPlace={(point) => (
              <PlaceCard
                point={point}
                selectedKey={resolved.display?.key ?? null}
                onPickFactory={(item) => select({ kind: 'factory', region: item.factory.region, site: item.factory.name })}
                onPick={(item, display) =>
                  select({ kind: 'display', region: item.factory.region, site: item.factory.name, display: display.key })
                }
              />
            )}
          />
        </Suspense>
      )}

      {/*
        화면을 고르면 그 화면의 판 — 오른쪽(휴대폰은 아래). 지도에서는 공장을 골라도 그 공장의 판(지도에는 공장 상자가 없다).
        그림판에서는 공장 상자가 그 일을 하므로 판을 열지 않는다.
      */}
      {!loading && resolved.factory && resolved.region && (display || view === 'map') ? (
        <aside className="site-detail float-panel" aria-label={display ? '고른 화면' : `고른 ${labels.site}`}>
          {display ? (
            <>
              <div className="mb-2 flex items-center gap-2">
                <p className="min-w-0 flex-1 truncate text-[12px] text-slate-500">
                  {resolved.region.name} › {resolved.factory.name} › {display.building}
                </p>
                <button
                  type="button"
                  className="site-tree-caret"
                  aria-label="닫기"
                  onClick={() => select({ kind: 'factory', region: resolved.region!.name, site: resolved.factory!.name })}
                >
                  <X className="h-3.5 w-3.5" strokeWidth={2.2} />
                </button>
              </div>
              {/* 건물 주소는 통합 모니터링(지도)의 자리다 — 그림판에서는 적지 않는다. */}
              <DisplayFocus
                display={display}
                building={view === 'map' ? displayBuilding : null}
                entries={entries}
                onEditAddress={editAddress}
                from={view === 'map' ? '/integrated' : '/wall'}
                hubLink={navCapabilities(me).includes('feature:hub')}
              />
            </>
          ) : (
            <FactoryPanel
              factory={resolved.factory}
              entries={entries}
              onEditAddress={editAddress}
              selectedKey={null}
              onPick={(display) => select({ kind: 'display', region: resolved.region!.name, site: resolved.factory!.name, display: display.key })}
              onClose={() => select({ kind: 'region', region: resolved.region!.name })}
            />
          )}
        </aside>
      ) : null}

      {/* 그림판 위에 뜬 판 — 빈 자리는 그림판이 끌기·휠을 받는다(판만 누를 수 있다). */}
      <div className="monitor-overlay">
        <div className="monitor-overlay-nav">
          <aside className="monitor-nav float-panel" aria-label="사업장">
            {loading ? <Loading /> : <SiteTree title={title} named={Boolean(sites.data?.name.trim())} tree={tree} totals={totals} resolved={resolved} onSelect={select} />}
          </aside>
        </div>
        <div className="monitor-overlay-top">
          <div className="draw-toolbar float-panel">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <p className="text-[13.5px] text-slate-600">
                {labels.region} <strong className="text-slate-900">{tree.length}</strong> · {labels.site}{' '}
                <strong className="text-slate-900">{tree.reduce((sum, region) => sum + region.factories.length, 0)}</strong> · 화면{' '}
                <strong className="text-slate-900">{totals.total}</strong>대 · 온라인 <strong className="text-emerald-700">{totals.online}</strong>
                {totals.offline > 0 ? (
                  <>
                    {' '}
                    · 오프라인 <strong className="text-rose-700">{totals.offline}</strong>
                  </>
                ) : null}
                {cut.length > 0 ? (
                  <span className="ml-2 text-slate-500" title={cut.map((source) => source.name).join(', ')}>
                    · 연결 끊김 서버 {cut.length}
                  </span>
                ) : null}
              </p>
              <HelpTip
                label="사업장 현황 도움말"
                text={
                  view === 'map'
                    ? fillSiteLabels('통합 모니터링 — 이 서버와 통합 관제에 등록한 원격 서버의 화면을 한 지도에 그립니다. 지도를 끌어서 옮기고, 마우스 휠·트랙패드 핀치·[−][+] 로 줄이고 키웁니다. 화면 노드는 {location} 주소 자리에 카드로 그려지고, 멀리 보면 점으로 접힙니다(누르면 그 카드가 제 크기로 펼쳐집니다). 왼쪽 나무나 카드 머리·화면 노드를 누르면 그 범위로 맞춰 갑니다. 원격 서버의 화면은 썸네일 없이 상태만 표시하고, 연결이 끊긴 서버는 마지막으로 수신한 값을 회색으로 표시합니다. 집계는 30초마다 갱신됩니다.', labels)
                    : fillSiteLabels('그림판을 끌어서 옮기고, 마우스 휠·트랙패드 핀치·[−][+] 로 줄이고 키웁니다(키보드: 화살표 이동, + − 배율, 0 전체 보기). 왼쪽 나무나 영역 제목·화면 노드를 누르면 그 범위로 맞춰 갑니다. 나무 맨 위의 조직 이름과 {region}·{site:은} [사업장·위치] 메뉴에서 정하고, {location:은} 각 화면에 입력된 위치입니다. 영역의 자리와 크기는 [배치 편집] 으로 바꿉니다(내 계정에만 저장). 집계는 30초마다 갱신됩니다.', labels)
                }
              />
              {canEdit ? (
                <button type="button" className="btn ml-auto" onClick={() => setConfiguring(true)}>
                  <Settings2 className="h-3.5 w-3.5" strokeWidth={2} />
                  사업장 구성
                </button>
              ) : null}
            </div>
            {tree.length > 0 ? <ScopeCrumbs title={title} resolved={resolved} onSelect={select} /> : null}
          </div>
          {local.error || sites.error || wall.error || hub.error || addressAction.error ? (
            <div className="float-panel flex flex-col gap-2">
              {addressAction.error ? <ErrorNote message={addressAction.error} onDismiss={addressAction.clearError} /> : null}
              {local.error ? <ErrorNote message={local.error} /> : null}
              {sites.error ? <ErrorNote message={sites.error} /> : null}
              {wall.error ? <ErrorNote message={`본 서버의 화면 그림을 불러오지 못했습니다: ${wall.error}`} /> : null}
              {hub.error ? <ErrorNote message={`원격 서버의 현황을 불러오지 못했습니다: ${hub.error}`} /> : null}
            </div>
          ) : null}
        </div>
      </div>

      {addressing ? (
        <AddressDialog
          client={client}
          title={`${addressing.name} 주소`}
          value={ownBuildingAddress(entries, addressing.source.id, addressing.location)}
          inherited={addressLine(sourceBaseAddress(entries, addressing.source.id)) || undefined}
          onApply={(next) => saveBuildingAddress(addressing, next)}
          onClose={() => setAddressing(null)}
        />
      ) : null}

      {configuring && sites.data ? (
        <SiteConfigModal
          client={client}
          sources={sources}
          entries={entries}
          revision={sites.data.revision}
          onClose={() => setConfiguring(false)}
          onSaved={() => {
            setConfiguring(false);
            sites.reload();
          }}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────── 왼쪽 나무 ─────────────────────────────── */

/**
 * 사업장 › 지역 › 공장 › 화면. 맨 위는 사업장 이름 — 누르면 전체 범위다. 지역·공장은 층마다 접고 편다. 이름을 누르면
 * 그 범위를 고르고(그림이 바뀐다), 화살표는 접기만 한다. 고른 범위의 위층은 저절로 펼친다 — 링크로 들어와도 나무에서
 * 어디인지 보인다.
 *
 * 좁은 화면에서는 나무가 그림판 위를 덮으므로 사업장 이름 옆에 화살표가 생기고 지역 목록을 접은 채로 연다. 넓은
 * 화면에서는 늘 펼쳐 둔다(접을 화살표가 없다).
 */
function SiteTree({
  title,
  named,
  tree,
  totals,
  resolved,
  onSelect,
}: {
  /** 조직 이름(비었으면 "전체"). */
  title: string;
  /** 조직 이름을 정했는가 — 정하지 않았으면 어디서 정하는지 알려 준다. */
  named: boolean;
  tree: RegionNode[];
  totals: Counts;
  resolved: ResolvedScope;
  onSelect: (scope: SiteScope) => void;
}): ReactNode {
  const [regionsOpen, setRegionsOpen] = useState(wideScreen);
  const labels = useSiteLabels();
  /*
    지역은 펼친 채, 공장은 접힌 채로 시작한다 — 원격 서버의 지역은 몇 초 늦게 들어오므로 "펼친 것" 이 아니라
    "기본에서 뒤집은 것" 을 적는다(늦게 온 지역도 펼친 채로 선다).
  */
  const [flipped, setFlipped] = useState<Set<string>>(() => new Set());
  const isOpen = (key: string): boolean => (key.startsWith('r:') ? !flipped.has(key) : flipped.has(key));
  const regionKey = resolved.region?.key ?? null;
  const factoryKey = resolved.factory?.key ?? null;
  useEffect(() => {
    if (!regionKey && !factoryKey) return;
    setFlipped((current) => {
      const next = new Set(current);
      if (regionKey) next.delete(`r:${regionKey}`);
      if (factoryKey) next.add(`f:${factoryKey}`);
      return next.size === current.size && [...next].every((key) => current.has(key)) ? current : next;
    });
  }, [regionKey, factoryKey]);
  const toggle = (key: string): void =>
    setFlipped((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const scope = resolved.scope;
  const isOn = (kind: SiteScope['kind'], match: boolean): boolean => scope.kind === kind && match;

  return (
    <ul className="site-tree" role="tree" aria-label="사업장 범위">
      <li role="treeitem" aria-selected={scope.kind === 'all'} aria-expanded={regionsOpen}>
        <TreeRow
          depth={0}
          head
          open={regionsOpen}
          onToggle={() => setRegionsOpen((open) => !open)}
          selected={scope.kind === 'all'}
          onSelect={() => onSelect({ kind: 'all' })}
          icon={<Building2 className="h-4 w-4" strokeWidth={2} />}
          label={title}
          hint={named ? undefined : '조직 이름은 [사업장·위치] 메뉴에서 정합니다'}
          counts={totals}
          stale={false}
        />
        <ul role="group" className={`site-tree-regions ${regionsOpen ? '' : 'is-folded'}`}>
          {tree.map((region) => {
            const regionOpen = isOpen(`r:${region.key}`);
            return (
              <li key={region.key} role="treeitem" aria-expanded={regionOpen} aria-selected={isOn('region', scope.kind !== 'all' && scope.region === region.name)}>
                <TreeRow
                  depth={0}
                  open={regionOpen}
                  onToggle={() => toggle(`r:${region.key}`)}
                  selected={isOn('region', scope.kind !== 'all' && scope.region === region.name)}
                  onSelect={() => onSelect({ kind: 'region', region: region.name })}
                  icon={<MapPin className="h-3.5 w-3.5" strokeWidth={2} />}
                  label={region.name}
                  muted={region.name === unnamedRegion(labels)}
                  counts={region}
                  stale={region.stale}
                />
                {regionOpen ? (
                  <ul role="group">
                    {region.factories.map((factory) => {
                      const factoryOpen = isOpen(`f:${factory.key}`);
                      const inFactory = (scope.kind === 'factory' || scope.kind === 'display') && scope.region === region.name && scope.site === factory.name;
                      const displays = factoryDisplays(factory);
                      const listed = factory.buildings.some((building) => building.displays !== null);
                      return (
                        <li key={factory.key} role="treeitem" aria-expanded={factoryOpen} aria-selected={inFactory && scope.kind === 'factory'}>
                          <TreeRow
                            depth={1}
                            open={factoryOpen}
                            onToggle={() => toggle(`f:${factory.key}`)}
                            selected={inFactory && scope.kind === 'factory'}
                            onSelect={() => onSelect({ kind: 'factory', region: region.name, site: factory.name })}
                            icon={factory.stale ? <WifiOff className="h-3.5 w-3.5" strokeWidth={2} /> : <Building className="h-3.5 w-3.5" strokeWidth={2} />}
                            label={factory.name}
                            counts={factory}
                            stale={factory.stale}
                          />
                          {factoryOpen ? (
                            <ul role="group">
                              {displays.map((display) => {
                                const selected = scope.kind === 'display' && inFactory && scope.display === display.key;
                                return (
                                  <li key={display.key} role="treeitem" aria-selected={selected}>
                                    <button
                                      type="button"
                                      className={`site-tree-row is-leaf ${selected ? 'is-on' : ''}`}
                                      style={{ paddingLeft: '2.6rem' }}
                                      onClick={() => onSelect({ kind: 'display', region: region.name, site: factory.name, display: display.key })}
                                      title={`${display.name || '(이름 없음)'} · ${display.building}`}
                                    >
                                      <StatusDot online={display.online} />
                                      <span className="min-w-0 flex-1 truncate">{display.name || '(이름 없음)'}</span>
                                      <span className="site-tree-meta truncate">{display.building}</span>
                                    </button>
                                  </li>
                                );
                              })}
                              {!listed ? <li className="site-tree-note">{factory.stale ? '연결이 끊겨 화면 목록이 없습니다' : '화면 목록을 받지 못했습니다'}</li> : null}
                            </ul>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </li>
    </ul>
  );
}

function TreeRow({
  depth,
  head = false,
  open,
  onToggle,
  selected,
  onSelect,
  icon,
  label,
  hint,
  muted = false,
  counts,
  stale,
}: {
  depth: number;
  /** 나무 맨 위의 조직 이름 줄 — 크게, 화살표는 좁은 화면에서만. */
  head?: boolean;
  open?: boolean;
  onToggle?: () => void;
  selected: boolean;
  onSelect: () => void;
  icon: ReactNode;
  label: string;
  /** 이름 위에 올리면 뜨는 도움말. */
  hint?: string;
  muted?: boolean;
  counts: Counts;
  stale: boolean;
}): ReactNode {
  const tone = healthTone(counts, stale);
  return (
    <div className={`site-tree-row ${head ? 'is-head' : ''} ${selected ? 'is-on' : ''}`} style={{ paddingLeft: `${0.25 + depth * 1.1}rem` }}>
      {onToggle ? (
        <button type="button" className="site-tree-caret" aria-label={`${label} ${open ? '접기' : '펼치기'}`} aria-expanded={open} onClick={onToggle}>
          <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`} strokeWidth={2.2} />
        </button>
      ) : (
        <span className="site-tree-caret" aria-hidden="true" />
      )}
      <button type="button" className="site-tree-label" onClick={onSelect} aria-current={selected ? 'true' : undefined} title={hint}>
        <span className="shrink-0 text-slate-400">{icon}</span>
        <span className={`min-w-0 flex-1 truncate ${muted ? 'text-slate-500' : ''}`}>{label}</span>
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONE_DOT[tone]}`} aria-hidden="true" />
        <span className="site-tree-meta">
          {counts.online}/{counts.total}
        </span>
      </button>
    </div>
  );
}

/* ─────────────────────────────── 지도 위에 뜬 판 ─────────────────────────────── */

const WIDE = '(min-width: 1024px)';
/** 좁은 화면에서는 나무가 그림 위를 덮으므로 지역 목록을 접은 채로 연다. */
const wideScreen = (): boolean => typeof window !== 'undefined' && window.matchMedia(WIDE).matches;

/**
 * 고른 공장의 판 — 지도 오른쪽(휴대폰은 아래)에 뜬다. 공장의 주소·숫자와 건물마다 화면 노드. 노드를 누르면 그 화면의 판.
 * 예전에는 그림판 위의 공장 영역이 이 일을 했다(2026-10-22 지도로 바꾸며 판으로 옮겼다).
 */
function FactoryPanel({
  factory,
  entries,
  onEditAddress,
  selectedKey,
  onPick,
  onClose,
}: {
  factory: FactoryNode;
  entries: readonly SiteEntry[];
  /** 건물 주소 창을 연다 — 고칠 수 없는 계정이면 없다. */
  onEditAddress?: (building: BuildingNode) => void;
  selectedKey: string | null;
  onPick: (display: SiteDisplay) => void;
  onClose: () => void;
}): ReactNode {
  const labels = useSiteLabels();
  const addresses = [...new Set(factory.buildings.map((building) => addressLine(building.address)).filter(Boolean))];
  const showSource = factory.sources.length > 1;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-[15px] font-semibold text-slate-900">
            <Building className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={2} aria-hidden="true" />
            <span className="truncate">{factory.name}</span>
          </p>
          <p className="mt-0.5 truncate text-[12px] text-slate-500">
            {factory.region}
            {factory.sources.map((source) => (
              <span key={source.id}>
                {' · '}
                {sourceLabel(source)}
                {source.kind === 'remote' ? (source.stale ? ' · 연결 끊김' : ' · 원격 서버') : null}
              </span>
            ))}
          </p>
        </div>
        <button type="button" className="site-tree-caret" aria-label="닫기" onClick={onClose}>
          <X className="h-3.5 w-3.5" strokeWidth={2.2} />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <CountLine counts={factory} stale={factory.stale} />
        <HealthBar counts={factory} stale={factory.stale} className="w-20" />
      </div>
      {addresses.length > 0 ? (
        <p className="flex items-start gap-1.5 text-[12.5px] text-slate-600">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
          <span className="min-w-0">{addresses.join(' · ')}</span>
        </p>
      ) : null}
      {factory.point ? null : (
        <p className="text-[12.5px] text-amber-700">
          {addresses.length > 0
            ? '주소에 좌표가 없어 지도에 표시되지 않습니다 — 주소 DB 에 위치정보요약DB 를 넣고 주소를 다시 고르거나, 좌표를 직접 적으세요.'
            : onEditAddress
              ? `주소가 없어 지도에 표시되지 않습니다 — 아래 ${labels.location}마다 [주소 정하기] 로 주소를 정하세요.`
              : `주소가 없어 지도에 표시되지 않습니다 — 화면 관리 권한이 있는 사람이 ${labels.location} 주소를 정해야 합니다.`}
        </p>
      )}
      {factory.buildings.map((building) => (
        <BuildingBlock
          key={building.key}
          building={building}
          own={ownBuildingAddress(entries, building.source.id, building.location)}
          onEditAddress={onEditAddress ? () => onEditAddress(building) : undefined}
          showSource={showSource}
          selectedKey={selectedKey}
          onPick={onPick}
        />
      ))}
    </div>
  );
}

/** 고른 범위 — 조직 › 지역 › 사업장 › 화면. 누르면 그 위 범위로. */
function ScopeCrumbs({
  title,
  resolved,
  onSelect,
}: {
  title: string;
  resolved: ResolvedScope;
  onSelect: (scope: SiteScope) => void;
}): ReactNode {
  const { region, factory, display } = resolved;
  return (
    <nav className="site-crumbs" aria-label="고른 범위">
      <button type="button" className={!region ? 'is-on' : ''} onClick={() => onSelect({ kind: 'all' })}>
        {title}
      </button>
      {region ? (
        <>
          <ChevronRight className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />
          <button type="button" className={!factory ? 'is-on' : ''} onClick={() => onSelect({ kind: 'region', region: region.name })}>
            {region.name}
          </button>
        </>
      ) : null}
      {region && factory ? (
        <>
          <ChevronRight className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />
          <button type="button" className={!display ? 'is-on' : ''} onClick={() => onSelect({ kind: 'factory', region: region.name, site: factory.name })}>
            {factory.name}
          </button>
        </>
      ) : null}
      {display ? (
        <>
          <ChevronRight className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />
          <span className="is-on">{display.name || '(이름 없음)'}</span>
        </>
      ) : null}
    </nav>
  );
}

/** 건물 — 공장 판 안의 한 칸. 이름표 아래에 그 건물의 화면 노드가 두 줄씩 선다. */
function BuildingBlock({
  building,
  own,
  onEditAddress,
  showSource,
  selectedKey,
  onPick,
}: {
  building: BuildingNode;
  /** 이 건물 줄에 직접 적은 주소 — 없으면 기본 주소를 따르는 중(`building.address`). */
  own: SiteAddress | null;
  onEditAddress?: () => void;
  showSource: boolean;
  selectedKey: string | null;
  onPick: (display: SiteDisplay) => void;
}): ReactNode {
  const stale = building.source.stale;
  const count = building.displays?.length ?? 0;
  return (
    <section className={`site-zone ${stale ? 'is-stale' : ''}`}>
      <p className="site-zone-head">
        <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" strokeWidth={2} aria-hidden="true" />
        <span className={`truncate ${building.location === '' ? 'text-slate-500' : 'text-slate-700'}`}>{building.name}</span>
        {showSource ? <span className="truncate text-slate-400">{sourceLabel(building.source)}</span> : null}
        <span className="ml-auto shrink-0">
          <CountLine counts={building} stale={stale} />
        </span>
      </p>
      <BuildingAddress building={building} own={own} onEdit={onEditAddress} />
      {building.displays && count > 0 ? (
        <div className="site-nodes">
          {building.displays.map((display) => (
            <DisplayNode key={display.key} display={display} selected={display.key === selectedKey} onPick={() => onPick(display)} />
          ))}
        </div>
      ) : (
        <p className="site-zone-note">
          {stale ? '연결이 끊겨 화면 목록을 받지 못했습니다 — 숫자는 끊기기 전의 것입니다.' : '화면 목록을 받지 못했습니다 — 숫자만 표시합니다.'}
        </p>
      )}
    </section>
  );
}

/**
 * 건물 주소 한 줄 — 직접 적은 주소, 없으면 따르는 기본 주소(흐리게), 그것도 없으면 "주소 없음". 아이콘 색은 지도에 서는가
 * (좌표 있음 초록·좌표 없음 주황·주소 없음 회색). 고칠 수 있으면 [주소 정하기]·[주소 바꾸기].
 */
function BuildingAddress({
  building,
  own,
  onEdit,
  plain = false,
}: {
  building: BuildingNode;
  own: SiteAddress | null;
  onEdit?: () => void;
  /** 화면 판의 표 칸 안 — 여백 없이. */
  plain?: boolean;
}): ReactNode {
  const shown = addressLine(own ?? building.address);
  const tone = building.point ? 'text-emerald-600' : shown ? 'text-amber-600' : 'text-slate-300';
  return (
    <span className={`site-zone-address ${plain ? 'is-plain' : ''}`}>
      <MapPin className={`h-3.5 w-3.5 shrink-0 ${tone}`} aria-hidden="true" />
      <span className={`min-w-0 flex-1 ${own ? 'text-slate-700' : 'text-slate-400'}`}>
        {shown ? (own ? shown : `${shown} (기본 주소)`) : '주소 없음 — 지도에 표시되지 않습니다'}
        {shown && !building.point ? <span className="text-amber-700"> · 좌표 없음</span> : null}
      </span>
      {onEdit ? (
        <button type="button" className="link shrink-0 text-[12px]" onClick={onEdit} aria-label={`${building.name} ${own ? '주소 바꾸기' : '주소 정하기'}`}>
          {own ? '주소 바꾸기' : '주소 정하기'}
        </button>
      ) : null}
    </span>
  );
}

/**
 * 지도 위 노드 카드 — 주소 한 자리(같은 좌표의 건물들)의 화면 노드를 그림판처럼 지도 위에 그린다(2026-10-22 사용자
 * 지시 "draw 형태인데 백그라운드 배경을 현재 지도로 표시하고 그 위에 각 화면 노드들을 적용"). 머리는 건물 이름과
 * 숫자 — 누르면 그 공장의 판. 건물이 여럿이면 건물마다 줄을 나눈다. 노드를 누르면 그 화면의 판.
 */
function PlaceCard({
  point,
  selectedKey,
  onPickFactory,
  onPick,
}: {
  point: AddressPoint;
  selectedKey: string | null;
  onPickFactory: (item: AddressPointItem) => void;
  onPick: (item: AddressPointItem, display: SiteDisplay) => void;
}): ReactNode {
  const several = point.items.length > 1;
  const factories = new Set(point.items.map((item) => item.factory.key));
  const nodes = (item: AddressPointItem): ReactNode => {
    const { building } = item;
    const displays = building.displays ?? [];
    if (displays.length === 0) {
      return (
        <p className="site-place-note">
          {building.source.stale ? '연결 끊김 — 화면 목록 없음' : '화면 목록을 받지 못했습니다'}
        </p>
      );
    }
    return (
      <div className="site-place-nodes" style={{ gridTemplateColumns: `repeat(${Math.min(displays.length, 3)}, var(--place-node-w))` }}>
        {displays.map((display) => (
          <DisplayNode key={display.key} display={display} selected={display.key === selectedKey} onPick={() => onPick(item, display)} />
        ))}
      </div>
    );
  };
  return (
    <div className="site-place-body">
      <button
        type="button"
        className="site-place-head"
        onClick={() => onPickFactory(point.items[0]!)}
        title={point.address || undefined}
      >
        <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" strokeWidth={2} aria-hidden="true" />
        <span className="min-w-0 truncate">{several ? point.items[0]!.factory.name : point.label}</span>
        <span className="ml-auto shrink-0">
          <CountLine counts={point} stale={point.stale} />
        </span>
      </button>
      {several
        ? point.items.map((item) => (
            <section key={item.building.key} className="site-place-building">
              <p className="site-place-building-head">
                <span className="truncate">{item.building.name}</span>
                {/* 한 자리에 두 공장의 건물이 있으면 어느 공장인지 — 하나면 군말이다. */}
                {factories.size > 1 ? <span className="truncate text-slate-400">{item.factory.name}</span> : null}
              </p>
              {nodes(item)}
            </section>
          ))
        : nodes(point.items[0]!)}
    </div>
  );
}

/** 화면 한 대를 크게 — 고른 화면의 그림과 상태, 그 화면으로 가는 길. */
function DisplayFocus({
  display,
  building,
  entries,
  onEditAddress,
  from,
  hubLink,
}: {
  display: SiteDisplay;
  /** 이 화면이 놓인 건물 — 주소 줄과 [주소 정하기] 에 쓴다. */
  building: BuildingNode | null;
  entries: readonly SiteEntry[];
  onEditAddress?: (building: BuildingNode) => void;
  /** 화면 상세의 [← 뒤로] 가 돌아올 메뉴 — 모니터링 또는 통합 모니터링. */
  from: string;
  /** 통합 관제를 열 수 있는가 — 통합 모니터링만 보는 관리자(모듈 비공개)에게는 그 길이 없다. */
  hubLink: boolean;
}): ReactNode {
  const labels = useSiteLabels();
  const source = display.source;
  return (
    <div className="site-focus">
      <div className="site-focus-shot">
        <NodeShot display={display} large />
      </div>
      <div className="flex min-w-0 flex-col gap-3">
        <p className="flex items-center gap-2 text-[15px] font-semibold text-slate-900">
          <StatusDot online={display.online} />
          <span className="truncate">{display.name || '(이름 없음)'}</span>
          <Badge tone={display.online ? 'ok' : 'danger'}>{display.online ? '온라인' : '오프라인'}</Badge>
        </p>
        <dl className="site-focus-facts">
          <dt>{labels.location}</dt>
          <dd>{display.building}</dd>
          {building ? (
            <>
              <dt>주소</dt>
              <dd>
                <BuildingAddress
                  building={building}
                  own={ownBuildingAddress(entries, building.source.id, building.location)}
                  onEdit={onEditAddress ? () => onEditAddress(building) : undefined}
                  plain
                />
              </dd>
            </>
          ) : null}
          <dt>서버</dt>
          <dd>{source.kind === 'local' ? LOCAL_SITE_NAME : `${source.name} · 원격 서버`}</dd>
          <dt>마지막 보고</dt>
          <dd>{display.lastSeen ? relativeTime(display.lastSeen) : '—'}</dd>
          <dt>버전</dt>
          <dd>{display.version || '—'}</dd>
          <dt>MAC</dt>
          <dd className={display.mac ? 'font-mono' : undefined}>{display.mac ?? '—'}</dd>
          {display.card?.now_playing ? (
            <>
              <dt>재생 중</dt>
              <dd className="truncate">{display.card.now_playing.title}</dd>
            </>
          ) : null}
          {display.error ? (
            <>
              <dt>오류</dt>
              <dd className="text-rose-700">{display.error}</dd>
            </>
          ) : null}
        </dl>
        {source.stale ? <Badge tone="neutral">연결 끊김 · {source.asOf ? `${relativeTime(source.asOf)} 기준` : '수신 기록 없음'}</Badge> : null}
        <div className="flex flex-wrap gap-2">
          {source.kind === 'local' ? (
            <Link className="btn btn-primary" to={`/displays/${display.id}`} state={{ from }}>
              화면 상세
            </Link>
          ) : hubLink ? (
            // 원격 서버의 화면에 명령을 보내는 자리는 통합 관제다 — 여기서는 현황만.
            <Link className="btn" to={`/hub?server=${encodeURIComponent(source.id)}`}>
              통합 관제에서 열기
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** [위치별 개별 지정] 이 접혀 있을 때 — 위치 수와 덮어쓴 곳. `word` 는 셋째 층의 이름. */
function buildingSummary(value: SiteDraft, word: string): string {
  const all = Object.values(value.buildings);
  const own = all.filter((place) => place.region.trim() || place.site.trim()).length;
  return own > 0 ? `${word} ${all.length}곳 · 개별 지정 ${own}곳` : `${word} ${all.length}곳 · 모두 서버 기본값`;
}

/**
 * 사업장 구성 — 서버(출처)마다 기본 지역·사업장, 그 아래 위치마다 덮어쓸 지역·사업장. 빈 칸은 위의 값을 따른다.
 * 위치 이름 자체는 각 서버의 화면 위치라 여기서 고치지 않는다(이 서버면 화면 상세에서, 다른 서버면 그 서버에서).
 * 층을 부르는 말은 이 서버에서 고른 것(`useSiteLabels`) — 다른 서버의 줄도 같은 말로 적는다.
 */
function SiteConfigModal({
  client,
  sources,
  entries,
  revision,
  onClose,
  onSaved,
}: {
  client: SignageOneClient;
  sources: SiteSource[];
  entries: SiteEntry[];
  revision: number;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const labels = useSiteLabels();
  const [draft, setDraft] = useState<Record<string, SiteDraft>>(() => draftFromEntries(sources, entries));
  /** 기본 주소 창을 연 출처. */
  const [addressing, setAddressing] = useState<string | null>(null);
  const action = useAction();
  const names = useMemo(() => {
    const regions = new Set<string>();
    const sites = new Set<string>();
    for (const value of Object.values(draft)) {
      for (const place of [value, ...Object.values(value.buildings)]) {
        if (place.region.trim()) regions.add(place.region.trim());
        if (place.site.trim()) sites.add(place.site.trim());
      }
    }
    return { regions: [...regions].sort(), sites: [...sites].sort() };
  }, [draft]);

  const setBase = (source: string, patch: Partial<Pick<SiteDraft, 'region' | 'site' | 'address'>>): void =>
    setDraft((current) => ({ ...current, [source]: { ...current[source]!, ...patch } }));
  const setBuilding = (source: string, location: string, patch: Partial<{ region: string; site: string }>): void =>
    setDraft((current) => {
      const value = current[source]!;
      return { ...current, [source]: { ...value, buildings: { ...value.buildings, [location]: { ...value.buildings[location]!, ...patch } } } };
    });

  return (
    <Modal title="사업장 구성" onClose={onClose} wide>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void action.run(() => client.saveSites({ entries: mergeSiteEntries(entries, draft), revision }), onSaved, '사업장 구성을 저장했습니다');
        }}
      >
        <p className="hint">
          {fillSiteLabels(
            '화면을 {region} › {site} › {location} 3단계로 분류합니다. {location:은} 각 화면에 입력된 위치입니다. 서버마다 기본 {region}·{site:을} 지정하고, 한 서버가 여러 {site:을} 관리하면 [{location}별 개별 지정]에서 {location}마다 지정합니다. 비워 둔 칸은 서버 기본값을 따릅니다. 이 서버의 위치는 [사업장·위치] 메뉴에서 위치 이름 바꾸기·지우기와 층 이름 고르기까지 한 표로 다룰 수 있습니다.',
            labels,
          )}
        </p>
        <datalist id="site-regions">
          {names.regions.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
        <datalist id="site-sites">
          {names.sites.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        {sources.map((source) => {
          const value = draft[source.id];
          if (!value) return null;
          const fallbackSite = source.kind === 'local' ? LOCAL_SITE_NAME : source.name;
          const baseRegion = value.region.trim() || unnamedRegion(labels);
          const baseSite = value.site.trim() || fallbackSite;
          return (
            <fieldset key={source.id} className="rounded-lg border border-slate-200">
              <legend className="ml-3 px-1 text-[13px] font-semibold text-slate-800">
                {source.kind === 'local' ? LOCAL_SITE_NAME : `${source.name} · 원격 서버`}
              </legend>
              <div className="grid gap-2 px-3.5 pb-3 pt-1 sm:grid-cols-2">
                <label className="field">
                  <span>기본 {labels.region}</span>
                  <input className="input" list="site-regions" placeholder={unnamedRegion(labels)} maxLength={60} value={value.region} onChange={(event) => setBase(source.id, { region: event.target.value })} />
                </label>
                <label className="field">
                  <span>기본 {labels.site}</span>
                  <input className="input" list="site-sites" placeholder={fallbackSite} maxLength={60} value={value.site} onChange={(event) => setBase(source.id, { site: event.target.value })} />
                </label>
                {/* 기본 주소 — 이 서버의 건물이 따로 주소를 갖지 않으면 지도의 이 자리에 선다. 건물별 주소는 그 서버의 [사업장·위치] 에서. */}
                <div className="field sm:col-span-2">
                  <span>기본 주소</span>
                  <button type="button" className="location-address" aria-label={`${source.kind === 'local' ? LOCAL_SITE_NAME : source.name} 기본 주소`} onClick={() => setAddressing(source.id)}>
                    <MapPin className={`h-3.5 w-3.5 shrink-0 ${pointOf(value.address) ? 'text-emerald-600' : 'text-slate-300'}`} aria-hidden="true" />
                    <span className={`min-w-0 truncate ${value.address ? 'text-slate-700' : 'text-slate-400'}`}>{addressLine(value.address) || '주소 없음 — 지도에 표시되지 않습니다'}</span>
                  </button>
                </div>
              </div>
              {Object.keys(value.buildings).length > 0 ? (
                /*
                  위치마다 덮어쓰는 일은 한 서버가 여러 사업장을 맡을 때뿐이다 — 펼친 채로 열고, 쓰지 않는 사람은
                  접는다(접으면 머리에 덮어쓴 곳 수).
                */
                <div className="border-t border-slate-100 px-3.5 py-2.5">
                  <Disclosure
                    className="is-plain"
                    title={`${labels.location}별 개별 지정`}
                    summary={buildingSummary(value, labels.location)}
                  >
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{labels.location === '위치' ? '위치' : `${labels.location}(위치)`}</th>
                          <th>{labels.region}</th>
                          <th>{labels.site}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(value.buildings)
                          .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : compareLocationNames(a, b)))
                          .map(([location, place]) => (
                            <tr key={location}>
                              <td className={location ? 'cell-strong' : 'cell-muted'}>{location || unassignedLocation(labels)}</td>
                              <td>
                                <input
                                  className="input w-full"
                                  list="site-regions"
                                  aria-label={`${location || unassignedLocation(labels)} ${labels.region}`}
                                  placeholder={baseRegion}
                                  maxLength={60}
                                  value={place.region}
                                  onChange={(event) => setBuilding(source.id, location, { region: event.target.value })}
                                />
                              </td>
                              <td>
                                <input
                                  className="input w-full"
                                  list="site-sites"
                                  aria-label={`${location || unassignedLocation(labels)} ${labels.site}`}
                                  placeholder={baseSite}
                                  maxLength={60}
                                  value={place.site}
                                  onChange={(event) => setBuilding(source.id, location, { site: event.target.value })}
                                />
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </Disclosure>
                </div>
              ) : (
                <p className="hint px-3.5 pb-3">{source.kind === 'remote' && source.stale ? `연결이 끊겨 ${labels.location} 목록을 받지 못했습니다.` : '등록된 화면이 없습니다.'}</p>
              )}
            </fieldset>
          );
        })}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.pending}>
            {action.pending ? '저장 중…' : '저장'}
          </button>
        </div>
      </form>
      {addressing && draft[addressing] ? (
        <AddressDialog
          client={client}
          title="기본 주소"
          value={draft[addressing]!.address}
          onApply={(next) => setBase(addressing, { address: next })}
          onClose={() => setAddressing(null)}
        />
      ) : null}
    </Modal>
  );
}
